import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bed, DollarSign, Stethoscope, FileText, Activity, Pill, ArrowLeftRight, FileSignature, Award, Scissors } from 'lucide-react';
import { admissionService, bedService } from '../../lib/services.ipd';
import { invoiceService } from '../../lib/services';
import type { Admission, BedAssignment } from '../../lib/services.ipd';
import type { Invoice } from '../../lib/supabase';
import toast from 'react-hot-toast';
import Tabs from '../../components/Tabs';
import NursingNotesTab from './components/NursingNotesTab';
import DoctorProgressTab from './components/DoctorProgressTab';
import VitalsTab from './components/VitalsTab';
import MedicationTab from './components/MedicationTab';
import TransfersTab from './components/TransfersTab';
import ConsentsTab from './components/ConsentsTab';
import CertificatesTab from './components/CertificatesTab';
import SurgeryTab from './components/SurgeryTab';

export default function AdmissionDetails() {
  const { formatCurrency } = useCurrency();
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const [admission, setAdmission] = useState<Admission | null>(null);
  const [bedAssignment, setBedAssignment] = useState<BedAssignment | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (admissionId) {
      loadAdmission();
    }
  }, [admissionId]);

  const loadAdmission = async () => {
    if (!admissionId) return;
    
    try {
      const [admissionData, bedData] = await Promise.all([
        admissionService.getById(admissionId),
        bedService.getCurrentAssignment(admissionId),
      ]);
      setAdmission(admissionData);
      setBedAssignment(bedData);
      
      if (admissionData) {
        try {
          const invoiceData = await invoiceService.getByReference(admissionId, 'admission');
          setInvoice(invoiceData);
        } catch (error) {
          console.error('Failed to load invoice:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load admission:', error);
      toast.error('Failed to load admission details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!admission) {
    return <div className="p-6">Admission not found</div>;
  }

  const tabs = [
    {
      id: 'nursing',
      label: 'Nursing Notes',
      icon: <Stethoscope className="w-4 h-4" />,
      content: <NursingNotesTab admissionId={admissionId!} />,
    },
    {
      id: 'doctor',
      label: 'Doctor Progress',
      icon: <FileText className="w-4 h-4" />,
      content: <DoctorProgressTab admissionId={admissionId!} />,
    },
    {
      id: 'vitals',
      label: 'Vitals',
      icon: <Activity className="w-4 h-4" />,
      content: <VitalsTab admissionId={admissionId!} />,
    },
    {
      id: 'medication',
      label: 'Medication',
      icon: <Pill className="w-4 h-4" />,
      content: <MedicationTab admissionId={admissionId!} />,
    },
    {
      id: 'transfers',
      label: 'Transfers',
      icon: <ArrowLeftRight className="w-4 h-4" />,
      content: <TransfersTab admissionId={admissionId!} />,
    },
    {
      id: 'consents',
      label: 'Consents',
      icon: <FileSignature className="w-4 h-4" />,
      content: <ConsentsTab admissionId={admissionId!} admission={admission} />,
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: <Award className="w-4 h-4" />,
      content: <CertificatesTab admissionId={admissionId!} />,
    },
    {
      id: 'surgery',
      label: 'Surgery',
      icon: <Scissors className="w-4 h-4" />,
      content: <SurgeryTab admissionId={admissionId!} />,
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ipd')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admissions
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Admission Details</h1>
        <p className="text-gray-600">{admission.admission_no}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Patient Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Patient Name</p>
                <p className="font-medium">{admission.patient?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Admission Date</p>
                <p className="font-medium">{new Date(admission.admission_date).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Doctor</p>
                <p className="font-medium">{admission.doctor?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-medium capitalize">{admission.status.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Clinical Information</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Chief Complaint</p>
                <p className="font-medium">{admission.chief_complaint || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Provisional Diagnosis</p>
                <p className="font-medium">{admission.provisional_diagnosis || 'Not specified'}</p>
              </div>
              {admission.notes && (
                <div>
                  <p className="text-sm text-gray-600">Notes</p>
                  <p className="font-medium">{admission.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Bed Assignment</h2>
            {bedAssignment ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Bed className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{bedAssignment.bed?.bed_no}</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Ward</p>
                  <p>{bedAssignment.bed?.ward?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Type</p>
                  <p className="capitalize">{bedAssignment.bed?.bed_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Daily Rate</p>
                  <p>₹{bedAssignment.bed?.daily_rate}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No bed assigned</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Billing & Invoice
            </h2>
            {invoice ? (
              <div className="space-y-4">
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-medium">{invoice.invoice_number}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Charges Breakdown</p>
                  <div className="space-y-2">
                    {invoice.items?.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {item.description} (x{item.quantity})
                        </span>
                        <span className="font-medium">₹{item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">₹{invoice.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax ({invoice.tax_rate}%)</span>
                    <span className="font-medium">₹{invoice.tax_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-lg">₹{invoice.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-blue-600">
                    <span>Balance Due</span>
                    <span className="font-semibold">₹{invoice.balance_due.toFixed(2)}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/billing')}
                  className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Full Invoice
                </button>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No billing charges yet</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <Tabs tabs={tabs} />
      </div>
    </div>
  );
}
